interface SearchBoxOptions {
  apiBaseUrl: string;
  inputSelector: string;
  lang?: string;
  upgatesResultsUrlTemplate?: string;
  maxProducts?: number;
  maxCategories?: number;
  maxTop?: number;
}

interface SuggestResponse {
  categories: Array<{ name: string; url: string; count: number }>;
  brands: Array<{ name: string; count: number }>;
  top: Array<{
    id: string;
    name: string;
    brand?: string;
    category_name?: string;
    image_url: string;
    price: number;
    currency: string;
    in_stock: boolean;
    availability?: string;
    url: string;
  }>;
  products: Array<{
    id: string;
    name: string;
    brand?: string;
    category_name?: string;
    image_url: string;
    price: number;
    currency: string;
    in_stock: boolean;
    availability?: string;
    url: string;
  }>;
  trending?: Array<{
    id: string;
    name: string;
    brand?: string;
    category_name?: string;
    image_url: string;
    price: number;
    currency: string;
    in_stock: boolean;
    availability?: string;
    url: string;
  }>;
  // AI metadata
  ai_correction?: {
    original: string;
    corrected: string;
  };
  ai_parse?: {
    intent: string;
    filters: any;
  };
}

class SearchBox {
  private options: Required<SearchBoxOptions>;
  private input: HTMLInputElement | null = null;
  private overlay: HTMLElement | null = null;
  private debounceTimer: number | null = null;
  private observer: MutationObserver | null = null;
  private selectedIndex: number = -1;
  private currentResults: SuggestResponse | null = null;
  private isOpen: boolean = false;
  private isMobile: boolean = false;
  private isLoading: boolean = false;
  private pollingInterval: number | null = null;
  private lastInputValue: string = '';
  
  // Backdrop overlay for mobile
  private backdrop: HTMLElement | null = null;
  
  // Touch gesture handling for mobile swipe-to-close
  private touchStartY: number = 0;
  private touchCurrentY: number = 0;
  private isDragging: boolean = false;

  constructor(options: SearchBoxOptions) {
    this.options = {
      lang: 'sk',
      upgatesResultsUrlTemplate: '/search?phrase={q}',
      maxProducts: 6,
      maxCategories: 8,
      maxTop: 3,
      ...options,
    };

    this.init();
  }

  private init(): void {
    this.detectMobile();
    console.log(`[SearchBox] Init started. Mobile: ${this.isMobile}`);
    
    // TEMPORARY: Disable custom search on mobile, only enable on desktop
    if (this.isMobile) {
      console.log(`[SearchBox] Mobile detected - custom search disabled, using default UPgates`);
      return; // Exit early on mobile, use default UPgates search
    }
    
    this.findInput();
    if (this.input) {
      console.log(`[SearchBox] Input found: ${this.input.className}`);
      this.attachListeners();
      this.setupMutationObserver();
    } else {
      console.log(`[SearchBox] Input NOT found!`);
    }
  }
  
  private disableUpgatesSearch(): void {
    // Try to find and disable UPgates search overlay/autocomplete
    console.log(`[SearchBox] Attempting to disable UPgates search...`);
    
    // Continuously remove UPgates overlays
    const removeUpgatesOverlays = () => {
      let removed = false;
      
      // Look for UPgates search elements and remove them from DOM
      const upgatesSearchSelectors = [
        '.upgates-search',
        '.upgates-autocomplete', 
        '[data-upgates-search]',
        '.search-suggestions',
        '.autocomplete-suggestions',
        '[class*="autocomplete"]',
        '[class*="suggestion"]',
        '[id*="autocomplete"]',
        // Common UPgates classes
        '.tt-menu',
        '.tt-dropdown-menu',
        '.search-dropdown',
      ];
      
      upgatesSearchSelectors.forEach(selector => {
        const elements = document.querySelectorAll(selector);
        elements.forEach(el => {
          // Check if it's not our overlay
          if (!el.classList.contains('searchbox-overlay')) {
            el.remove();
            removed = true;
            console.log(`[SearchBox] Removed UPgates: ${selector}`);
          }
        });
      });
      
      return removed;
    };
    
    // Remove immediately
    removeUpgatesOverlays();
    
    // Keep checking and removing (UPgates might re-add them)
    setInterval(() => {
      if (this.input && document.activeElement === this.input) {
        removeUpgatesOverlays();
      }
    }, 100);
    
    // Also observe DOM for new UPgates elements
    const observer = new MutationObserver(() => {
      removeUpgatesOverlays();
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
    
    // Inject CSS to hide UPgates autocomplete globally
    const style = document.createElement('style');
    style.textContent = `
      .tt-menu, .tt-dropdown-menu, .search-dropdown,
      [class*="autocomplete"], [class*="suggestion"],
      .upgates-search, .upgates-autocomplete,
      .search-suggestions, .autocomplete-suggestions {
        display: none !important;
        visibility: hidden !important;
        opacity: 0 !important;
        pointer-events: none !important;
      }
    `;
    document.head.appendChild(style);
  }
  
  private startPolling(): void {
    let pollCount = 0;
    let lastFoundInputs = new Set<HTMLInputElement>();
    
    // On mobile, add input observer
    if (this.input && this.isMobile) {
      this.observeInputElement(this.input);
    }
    
    // Poll input value every 1000ms on mobile (reduced frequency)
    this.pollingInterval = window.setInterval(() => {
      pollCount++;
      
      // MOBILE: Search for ANY input with value (only every 5 seconds)
      if (this.isMobile && pollCount % 5 === 0) {
        const allInputs = document.querySelectorAll('input[type="text"], input[type="search"], input:not([type])');
        let foundValue = false;
        
        for (const inp of Array.from(allInputs)) {
          if (inp instanceof HTMLInputElement && inp.value && inp.value.length > 0) {
            if (!lastFoundInputs.has(inp)) {
              console.log(`[SearchBox] Found input with value: "${inp.value}"`);
              lastFoundInputs.add(inp);
            }
            
            // Use this value!
            if (inp.value !== this.lastInputValue) {
              console.log(`[SearchBox] Using: "${inp.value}"`);
              this.lastInputValue = inp.value;
              this.handleInputChange(inp.value);
              foundValue = true;
              break;
            }
          }
        }
        
        // Don't auto-close on mobile - keep results visible
        if (!foundValue && this.lastInputValue) {
          console.log(`[SearchBox] All inputs empty`);
          this.lastInputValue = '';
          // Don't close immediately on mobile to keep results stable
        }
      }

      if (!this.input) {
        this.findInput();
        if (this.input) {
          this.attachListeners();
          if (this.isMobile) {
            this.observeInputElement(this.input);
          }
        }
        return;
      }
      
      // Standard polling for desktop
      if (!this.isMobile) {
        let currentValue = this.input.value || '';
        
        if (pollCount % 10 === 0 && !currentValue) {
          // No value in main input
        }
        
        if (currentValue && currentValue !== this.lastInputValue) {
          this.lastInputValue = currentValue;
          this.handleInputChange(currentValue);
        }
      }
    }, 1000);
  }
  
  private observeInputElement(input: HTMLInputElement): void {
    console.log(`[SearchBox] Watching input for DOM changes`);
    
    // Watch for attribute changes on the input
    const inputObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
          const newValue = input.getAttribute('value') || '';
          if (newValue && newValue !== this.lastInputValue) {
            console.log(`[SearchBox] Attr changed: "${newValue}"`);
            this.lastInputValue = newValue;
            this.handleInputChange(newValue);
          }
        }
      }
    });
    
    inputObserver.observe(input, {
      attributes: true,
      attributeFilter: ['value'],
    });
  }
  
  private handleInputChange(value: string): void {
    if (this.debounceTimer) {
      window.clearTimeout(this.debounceTimer);
    }

    const query = value.trim();
    
    // Increased debounce time for better performance with AI
    // Short queries (< 5 chars): 200ms debounce (fast, no AI)
    // Long queries (5+ chars): 400ms debounce (AI processing)
    const debounceTime = query.length >= 5 ? 400 : 200;
    const minLength = this.isMobile ? 2 : 1;
    
    this.debounceTimer = window.setTimeout(() => {
      if (query.length >= minLength) {
        this.search(query);
      } else {
        this.close();
      }
    }, debounceTime);
  }
  
  private showDebugNotification(message: string): void {
    // Only log to console, no visual notifications
    console.log(`[SearchBox] ${message}`);
  }

  private detectMobile(): void {
    // Detect mobile device
    const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
    const isMobileDevice = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent.toLowerCase());
    const isSmallScreen = window.innerWidth <= 768;
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    
    // Consider mobile if: mobile device OR small screen (regardless of touch)
    this.isMobile = isMobileDevice || isSmallScreen;
    
    console.log(`[SearchBox] Mobile detection: isMobileDevice=${isMobileDevice}, isSmallScreen=${isSmallScreen}, isTouchDevice=${isTouchDevice}, FINAL isMobile=${this.isMobile}`);
    
    if (this.isMobile) {
      document.body.classList.add('searchbox-mobile');
    } else {
      document.body.classList.remove('searchbox-mobile');
    }
  }

  private findInput(): void {
    const element = document.querySelector(this.options.inputSelector);
    if (element instanceof HTMLInputElement) {
      this.input = element;
      console.log(`[SearchBox] Found primary input: ${this.input.className}`);
      
      // MOBILE: Intercept UPgates - but DON'T clone, just mark and disable
      if (this.isMobile) {
        this.interceptUpgatesEvents(this.input);
        // After intercepting, re-get the input reference
        const updatedInput = document.querySelector(this.options.inputSelector);
        if (updatedInput instanceof HTMLInputElement) {
          this.input = updatedInput;
        }
      }
      return;
    }
    
    // Try common UPgates selectors
    const alternatives = [
      'input[name*="search"]',
      'input[name*="phrase"]',
      'input[type="search"]',
      '.search input',
      '#search input',
      'input.frmsearchForm-phrase',
      'input[name="phrase"]',
    ];
    
    for (const alt of alternatives) {
      const altElement = document.querySelector(alt);
      if (altElement instanceof HTMLInputElement) {
        this.input = altElement;
        console.log(`[SearchBox] Found alt input: ${alt}`);
        
        if (this.isMobile) {
          this.interceptUpgatesEvents(this.input);
        }
        return;
      }
    }
    
    // Last resort: find any input that looks like a search input
    const allInputs = document.querySelectorAll('input[type="text"], input[type="search"]');
    for (const input of Array.from(allInputs)) {
      if (input instanceof HTMLInputElement) {
        const placeholder = (input.placeholder || '').toLowerCase();
        const name = (input.name || '').toLowerCase();
        const className = (input.className || '').toLowerCase();
        const id = (input.id || '').toLowerCase();
        
        if (
          placeholder.includes('hľadať') || placeholder.includes('search') || placeholder.includes('vyhľadať') ||
          name.includes('search') || name.includes('phrase') ||
          className.includes('search') || className.includes('phrase') ||
          id.includes('search') || id.includes('phrase')
        ) {
          this.input = input;
          console.log(`[SearchBox] Found search-like input`);
          
          if (this.isMobile) {
            this.interceptUpgatesEvents(this.input);
          }
          return;
        }
      }
    }
  }
  
  private interceptUpgatesEvents(input: HTMLInputElement): void {
    console.log(`[SearchBox] Intercepting UPgates events on mobile`);
    
    // Disable autocomplete
    input.setAttribute('autocomplete', 'off');
    input.setAttribute('data-custom-search', 'true');
    
    // Add marker for UPgates
    (input as any).__customSearchEnabled = true;
    
    // Remove UPgates data attributes that trigger their autocomplete
    input.removeAttribute('data-tt');
    input.removeAttribute('data-typeahead');
    input.removeAttribute('data-autocomplete');
    input.removeAttribute('data-upgates-search');
    
    console.log(`[SearchBox] UPgates attributes removed from input`);
  }

  private setupMutationObserver(): void {
    this.observer = new MutationObserver(() => {
      if (!this.input || !document.contains(this.input)) {
        this.findInput();
        if (this.input) {
          this.attachListeners();
        }
      }
    });

    this.observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  private attachListeners(): void {
    if (!this.input) return;

    console.log(`[SearchBox] Attaching listeners to input`);

    // CAPTURE PHASE - intercept BEFORE UPgates handlers
    const inputHandler = (e: Event) => {
      console.log(`[SearchBox] Input event CAPTURED! Value: ${this.input?.value}`);
      this.handleInput();
      // Don't stop propagation - let UPgates work too for fallback
    };
    
    this.input.addEventListener('input', inputHandler, { capture: true });
    
    // MOBILE: Add keyup listener for better detection
    if (this.isMobile) {
      const keyupHandler = () => {
        console.log(`[SearchBox] Keyup event - Value: ${this.input?.value}`);
        if (this.input) {
          const value = this.input.value || '';
          if (value !== this.lastInputValue) {
            this.lastInputValue = value;
            this.handleInputChange(value);
          }
        }
      };
      
      this.input.addEventListener('keyup', keyupHandler, { capture: true });
      this.input.addEventListener('change', () => this.handleInput(), { capture: true });
      this.input.addEventListener('paste', () => {
        setTimeout(() => this.handleInput(), 10);
      }, { capture: true });
    }
    
    this.input.addEventListener('focus', () => {
      console.log(`[SearchBox] Focus event fired`);
      if (this.input?.value.trim()) {
        this.handleInput();
      }
    });
    
    this.input.addEventListener('keydown', (e) => this.handleKeydown(e));
    
    // Close on outside click
    document.addEventListener('click', (e) => {
      if (this.overlay && !this.overlay.contains(e.target as Node) && e.target !== this.input) {
        this.close();
      }
    });

    // Close on Escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });
  }

  private handleInput(): void {
    if (!this.input) return;

    const query = this.input.value.trim();

    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    this.debounceTimer = window.setTimeout(() => {
      if (query.length >= 1) {
        this.search(query);
      } else {
        this.close();
      }
    }, 200);
  }

  private async search(query: string): Promise<void> {
    if (!this.input) return;

    this.showDebugNotification(`🔎 Searching for: "${query}"`);

    // Show loading state
    this.isLoading = true;
    if (!this.overlay) {
      this.showDebugNotification(`📦 Creating overlay...`);
      this.createOverlay();
    }
    this.show();
    this.renderLoading();

    try {
      const url = new URL(`${this.options.apiBaseUrl}/suggest`);
      url.searchParams.set('q', query);
      url.searchParams.set('maxProducts', String(this.options.maxProducts));
      url.searchParams.set('maxCategories', String(this.options.maxCategories));
      url.searchParams.set('maxTop', String(this.options.maxTop));

      this.showDebugNotification(`🌐 Fetching: ${url.pathname}${url.search}`);
      
      // Add timeout to prevent hanging
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5s timeout
      
      const response = await fetch(url.toString(), {
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data: SuggestResponse = await response.json();
      
      this.showDebugNotification(`📊 Got ${data.products.length} products`);
      
      this.currentResults = data;
      this.isLoading = false;

      // Check if we have any results
      const hasResults = data.categories.length > 0 || data.top.length > 0 || data.products.length > 0;
      if (hasResults) {
        this.render(data);
        this.show();
        console.log(`[SearchBox] Displayed results`);
      } else {
        this.renderEmpty(query);
        console.log(`[SearchBox] No results`);
      }

      // Track search event
      this.trackSearch(query, data);
    } catch (error) {
      this.isLoading = false;
      // Show error in overlay instead of closing
      if (this.overlay) {
        const container = this.overlay.querySelector('.searchbox-container');
        if (container) {
          let errorMessage = 'Neznáma chyba';
          
          if (error instanceof Error) {
            if (error.name === 'AbortError') {
              errorMessage = 'Vyhľadávanie trvá príliš dlho. Skúste to znova.';
            } else {
              errorMessage = error.message;
            }
          }
          
          container.innerHTML = `
            <div class="searchbox-empty">
              <div class="searchbox-empty-icon">⚠️</div>
              <div class="searchbox-empty-text">Chyba pri načítaní výsledkov: ${this.escapeHtml(errorMessage)}</div>
            </div>
          `;
          this.show();
        }
      }
    }
  }

  private render(data: SuggestResponse): void {
    if (!this.input) return;

    if (!this.overlay) {
      this.createOverlay();
    }

    const container = this.overlay!.querySelector('.searchbox-container');
    if (!container) {
      return;
    }

    // MOBILE: Simple list rendering (dropdown below search box)
    if (this.isMobile) {
      let html = `
        <div style="padding: 15px 20px 20px 20px; background: white;">
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 15px;">
            <div style="font-size: 15px; color: #666; font-weight: 500;">Výsledky: ${data.products.length}</div>
            <button onclick="this.closest('.searchbox-overlay').style.display='none'" 
                    style="font-size: 28px; background: none; border: none; padding: 0; 
                           width: 36px; height: 36px; line-height: 1; color: #666; cursor: pointer;">×</button>
          </div>
      `;
      
      // AI Correction notification
      if (data.ai_correction) {
        html += `
          <div style="background: #e3f2fd; padding: 10px 12px; border-radius: 6px; margin-bottom: 12px; font-size: 13px; color: #1565c0;">
            ✨ Hľadáte: <strong>${this.escapeHtml(data.ai_correction.corrected)}</strong>
          </div>
        `;
      }
      
      data.products.forEach(product => {
        const imgUrl = product.image_url || '';
        html += `
          <a href="${product.url}" style="display: flex; padding: 15px 0; border-bottom: 1px solid #eee; 
                                          text-decoration: none; color: inherit; gap: 15px;">
            ${imgUrl ? `<img src="${imgUrl}" alt="${this.escapeHtml(product.name)}" 
                 style="width: 60px; height: 60px; object-fit: contain; flex-shrink: 0;">` : ''}
            <div style="flex: 1; min-width: 0;">
              <div style="font-size: 14px; line-height: 1.4; margin-bottom: 5px; 
                          overflow: hidden; text-overflow: ellipsis;">${this.escapeHtml(product.name)}</div>
              <div style="font-size: 16px; font-weight: bold; color: #c41e3a;">
                ${product.price.toFixed(2)} ${product.currency}
              </div>
              ${!product.in_stock ? '<div style="font-size: 12px; color: #999; margin-top: 3px;">Nie je skladom</div>' : ''}
            </div>
          </a>
        `;
      });
      
      html += `</div>`;
      container.innerHTML = html;
      return;
    }

    // Clear previous results
    container.innerHTML = '';

    // AI Correction banner (desktop)
    if (data.ai_correction) {
      const banner = document.createElement('div');
      banner.className = 'searchbox-ai-correction';
      banner.innerHTML = `
        <div class="ai-correction-icon">✨</div>
        <div class="ai-correction-text">
          Hľadáte: <strong>${this.escapeHtml(data.ai_correction.corrected)}</strong>
          <span class="ai-correction-original">(pôvodne: ${this.escapeHtml(data.ai_correction.original)})</span>
        </div>
      `;
      container.appendChild(banner);
    }

    // Render 3 columns
    const columns = document.createElement('div');
    columns.className = 'searchbox-columns';

    // Categories column
    if (data.categories.length > 0) {
      const catCol = this.renderCategories(data.categories);
      columns.appendChild(catCol);
    }

    // Top products column
    if (data.top.length > 0) {
      const topCol = this.renderTopProducts(data.top);
      columns.appendChild(topCol);
    }

    // Products column
    if (data.products.length > 0) {
      const prodCol = this.renderProducts(data.products);
      columns.appendChild(prodCol);
    }

    // Recommendations section
    let productIdForRecommendations: string | null = null;
    
    const clickedProductId = sessionStorage.getItem('lastClickedProductId');
    if (clickedProductId) {
      productIdForRecommendations = clickedProductId;
    }
    else if (data.top.length > 0 && data.top[0].id) {
      productIdForRecommendations = data.top[0].id;
    } else if (data.products.length > 0 && data.products[0].id) {
      productIdForRecommendations = data.products[0].id;
    }
    
    const categoriesColumn = columns.querySelector('.searchbox-categories');
    if (categoriesColumn && productIdForRecommendations) {
      this.loadAndRenderRecommendations(categoriesColumn, productIdForRecommendations, true);
    }

    container.appendChild(columns);

    // "All results" button
    const allResultsBtn = document.createElement('div');
    allResultsBtn.className = 'searchbox-all-results';
    const query = this.input.value.trim();
    const resultsUrl = this.options.upgatesResultsUrlTemplate.replace('{q}', encodeURIComponent(query));
    allResultsBtn.innerHTML = `<a href="${this.escapeHtml(resultsUrl)}">Všetky výsledky</a>`;
    container.appendChild(allResultsBtn);

    // "Powered by" footer
    const poweredBy = document.createElement('div');
    poweredBy.className = 'searchbox-powered-by';
    poweredBy.innerHTML = '<span>Powered by <strong>INPARFEMY</strong></span>';
    container.appendChild(poweredBy);

    this.show();
  }

  private renderLoading(): void {
    if (!this.overlay) return;

    const container = this.overlay.querySelector('.searchbox-container');
    if (!container) return;

    container.innerHTML = `
      <div class="searchbox-loading">
        <div class="searchbox-loading-spinner"></div>
        <div class="searchbox-loading-text">Hľadám...</div>
      </div>
    `;
  }

  private renderEmpty(query: string): void {
    if (!this.overlay || !this.input) return;

    const container = this.overlay.querySelector('.searchbox-container');
    if (!container) return;

    container.innerHTML = `
      <div class="searchbox-empty">
        <div class="searchbox-empty-icon">🔍</div>
        <div class="searchbox-empty-text">Nenašli sme žiadne výsledky pre <strong>${this.escapeHtml(query)}</strong></div>
        <div class="searchbox-all-results">
          <a href="${this.escapeHtml(this.options.upgatesResultsUrlTemplate.replace('{q}', encodeURIComponent(query)))}">Zobraziť všetky výsledky</a>
        </div>
      </div>
    `;
    this.show();
  }

  private renderCategoriesWithBrands(categories: SuggestResponse['categories'], brands: SuggestResponse['brands']): HTMLElement {
    const col = document.createElement('div');
    col.className = 'searchbox-column searchbox-categories';

    // Categories section
    if (categories.length > 0) {
      const title = document.createElement('div');
      title.className = 'searchbox-column-title';
      title.textContent = 'Kategórie';
      col.appendChild(title);

      const list = document.createElement('div');
      list.className = 'searchbox-list';

      categories.forEach((cat) => {
        const item = document.createElement('a');
        const query = this.input.value.trim();
        let categoryUrl = cat.url;
        if (query && categoryUrl.includes('/search')) {
          const urlObj = new URL(categoryUrl, window.location.origin);
          urlObj.searchParams.set('phrase', query);
          categoryUrl = urlObj.pathname + urlObj.search;
        }
        
        item.href = categoryUrl;
        item.className = 'searchbox-item searchbox-category-item';
        item.innerHTML = `
          <div class="searchbox-category-name">${this.escapeHtml(cat.name)}</div>
          <div class="searchbox-category-count">${cat.count}</div>
        `;
        
        item.addEventListener('click', () => {
          this.trackClick(undefined, cat.name);
        });
        list.appendChild(item);
      });

      col.appendChild(list);
    }

    // Brands section (below categories)
    if (brands.length > 0) {
      const brandsTitle = document.createElement('div');
      brandsTitle.className = 'searchbox-column-title';
      brandsTitle.style.marginTop = categories.length > 0 ? '20px' : '0';
      brandsTitle.textContent = 'Značky';
      col.appendChild(brandsTitle);

      const brandsList = document.createElement('div');
      brandsList.className = 'searchbox-list';

      brands.forEach((brand) => {
        const item = document.createElement('div');
        item.className = 'searchbox-item searchbox-brand-item';
        
        item.innerHTML = `
          <div class="searchbox-brand-name">${this.escapeHtml(brand.name)}</div>
          <div class="searchbox-brand-count">${brand.count}</div>
        `;
        
        item.addEventListener('click', () => {
          // Filter results by this brand
          this.input!.value = brand.name;
          this.input!.dispatchEvent(new Event('input', { bubbles: true }));
          this.trackClick(undefined, brand.name);
        });
        
        brandsList.appendChild(item);
      });

      col.appendChild(brandsList);
    }

    // If no categories and no brands, show suggestions
    if (categories.length === 0 && brands.length === 0) {
      const title = document.createElement('div');
      title.className = 'searchbox-column-title';
      title.textContent = 'Kategórie';
      col.appendChild(title);

      const list = document.createElement('div');
      list.className = 'searchbox-list';

      const suggestions = this.getSearchSuggestions();
      suggestions.forEach((suggestion) => {
        const item = document.createElement('div');
        item.className = 'searchbox-item searchbox-suggestion-item';
        item.textContent = suggestion;
        item.addEventListener('click', () => {
          this.input!.value = suggestion;
          this.input!.dispatchEvent(new Event('input', { bubbles: true }));
        });
        list.appendChild(item);
      });

      col.appendChild(list);
    }

    return col;
  }

  private renderCategories(categories: SuggestResponse['categories']): HTMLElement {
    const col = document.createElement('div');
    col.className = 'searchbox-column searchbox-categories';

    const title = document.createElement('div');
    title.className = 'searchbox-column-title';
    title.textContent = 'Kategórie';
    col.appendChild(title);

    const list = document.createElement('div');
    list.className = 'searchbox-list';

    if (categories.length === 0) {
      const suggestions = this.getSearchSuggestions();
      suggestions.forEach((suggestion) => {
        const item = document.createElement('div');
        item.className = 'searchbox-item searchbox-suggestion-item';
        item.textContent = suggestion;
        item.addEventListener('click', () => {
          this.input!.value = suggestion;
          this.input!.dispatchEvent(new Event('input', { bubbles: true }));
        });
        list.appendChild(item);
      });
    } else {
      categories.forEach((cat) => {
        const item = document.createElement('a');
        const query = this.input.value.trim();
        let categoryUrl = cat.url;
        if (query && categoryUrl.includes('/search')) {
          const urlObj = new URL(categoryUrl, window.location.origin);
          urlObj.searchParams.set('phrase', query);
          urlObj.searchParams.set('category', cat.name);
          categoryUrl = urlObj.pathname + urlObj.search;
        } else if (query) {
          categoryUrl = `/search?phrase=${encodeURIComponent(query)}&category=${encodeURIComponent(cat.name)}`;
        }
        item.href = this.escapeHtml(categoryUrl);
        item.className = 'searchbox-item searchbox-category-item';
        item.innerHTML = `
          <span class="searchbox-category-name">${this.escapeHtml(cat.name)}</span>
          <span class="searchbox-category-count">${cat.count}</span>
        `;
        item.addEventListener('click', () => {
          this.trackClick(undefined, cat.name);
        });
        list.appendChild(item);
      });
    }

    col.appendChild(list);
    return col;
  }

  private renderBrands(brands: SuggestResponse['brands']): HTMLElement {
    const col = document.createElement('div');
    col.className = 'searchbox-column searchbox-brands';

    const title = document.createElement('div');
    title.className = 'searchbox-column-title';
    title.textContent = 'Značky';
    col.appendChild(title);

    const list = document.createElement('div');
    list.className = 'searchbox-list';

    brands.forEach((brand) => {
      const item = document.createElement('div');
      item.className = 'searchbox-item searchbox-brand-item';
      
      item.innerHTML = `
        <div class="searchbox-brand-name">${this.escapeHtml(brand.name)}</div>
        <div class="searchbox-brand-count">${brand.count}</div>
      `;
      
      item.addEventListener('click', () => {
        // Filter results by this brand
        this.input!.value = brand.name;
        this.input!.dispatchEvent(new Event('input', { bubbles: true }));
        this.trackClick(undefined, brand.name);
      });
      
      list.appendChild(item);
    });

    col.appendChild(list);
    return col;
  }

  private getSearchSuggestions(): string[] {
    const query = this.input?.value.trim().toLowerCase() || '';
    const suggestions: string[] = [];
    
    if (query.length >= 2) {
      suggestions.push('Parfumy');
      suggestions.push('Dámske parfumy');
      suggestions.push('Pánske parfumy');
    }
    
    return suggestions.slice(0, 4);
  }

  private renderTopProducts(products: SuggestResponse['top']): HTMLElement {
    const col = document.createElement('div');
    col.className = 'searchbox-column searchbox-top';

    const title = document.createElement('div');
    title.className = 'searchbox-column-title';
    title.textContent = 'Najpredávanejšie';
    col.appendChild(title);

    const list = document.createElement('div');
    list.className = 'searchbox-list';

    products.forEach((product) => {
      const item = document.createElement('div');
      item.className = 'searchbox-item searchbox-top-item';
      item.dataset.url = product.url;
      item.dataset.index = String(this.selectedIndex + 1);
      
      item.innerHTML = `
        <div class="searchbox-top-image">
          <img src="${this.escapeHtml(product.image_url)}" alt="${this.escapeHtml(product.name)}" onerror="this.style.display='none'">
        </div>
        <div class="searchbox-top-info">
          <div class="searchbox-top-name">${this.escapeHtml(product.name)}</div>
          <div class="searchbox-top-price">${this.formatPrice(product.price, product.currency)}</div>
          ${product.availability ? `<div class="searchbox-stock">${this.escapeHtml(product.availability)}</div>` : (product.in_stock ? '<div class="searchbox-stock">Skladom</div>' : '')}
        </div>
        <div class="searchbox-top-cart">
          <button type="button" class="searchbox-cart-btn" data-product-url="${this.escapeHtml(product.url)}" aria-label="Pridať do košíka">
            🛒
          </button>
        </div>
      `;

      item.addEventListener('click', (e) => {
        if ((e.target as HTMLElement).closest('.searchbox-cart-btn')) {
          e.stopPropagation();
          this.handleAddToCart(product.url);
        } else {
          this.trackClick(product.id);
          window.location.href = product.url;
        }
      });

      list.appendChild(item);
    });

    col.appendChild(list);
    return col;
  }

  private highlightText(text: string, query: string): string {
    if (!query || query.length < 2) return this.escapeHtml(text);
    
    // Escape HTML first
    const escaped = this.escapeHtml(text);
    
    // Split query into words
    const words = query.toLowerCase().split(/\s+/).filter(w => w.length > 1);
    if (words.length === 0) return escaped;
    
    // Create regex for all query words (case-insensitive)
    const pattern = words.map(w => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|');
    const regex = new RegExp(`(${pattern})`, 'gi');
    
    // Replace matches with highlighted spans
    return escaped.replace(regex, '<mark class="searchbox-highlight">$1</mark>');
  }

  private renderProducts(products: SuggestResponse['products']): HTMLElement {
    const col = document.createElement('div');
    col.className = 'searchbox-column searchbox-products';

    const title = document.createElement('div');
    title.className = 'searchbox-column-title';
    title.textContent = 'Produkty';
    col.appendChild(title);

    const list = document.createElement('div');
    list.className = 'searchbox-list';

    products.forEach((product, index) => {
      const item = document.createElement('div');
      item.className = 'searchbox-item searchbox-product-item';
      item.dataset.url = product.url;
      item.dataset.index = String(this.selectedIndex + products.length + index + 1);

      const imageHtml = product.image_url
        ? `<div class="searchbox-product-image"><img src="${this.escapeHtml(product.image_url)}" alt="${this.escapeHtml(product.name)}" onerror="this.style.display='none'"></div>`
        : '';

      item.innerHTML = `
        ${imageHtml}
        <div class="searchbox-product-info">
          <div class="searchbox-product-name">${this.highlightText(product.name, this.lastQuery)}</div>
          ${product.brand ? `<div class="searchbox-product-brand">${this.escapeHtml(product.brand)}</div>` : ''}
          <div class="searchbox-product-meta">
            <span class="searchbox-product-price">${this.formatPrice(product.price, product.currency)}</span>
            ${product.availability ? `<span class="searchbox-stock">${this.escapeHtml(product.availability)}</span>` : (product.in_stock ? '<span class="searchbox-stock">Skladom</span>' : '')}
          </div>
        </div>
        <div class="searchbox-product-cart">
          <button type="button" class="searchbox-cart-btn" data-product-url="${this.escapeHtml(product.url)}" aria-label="Pridať do košíka">
            🛒
          </button>
        </div>
      `;

      item.addEventListener('click', (e) => {
        if ((e.target as HTMLElement).closest('.searchbox-cart-btn')) {
          e.stopPropagation();
          this.handleAddToCart(product.url);
        } else {
          this.trackClick(product.id);
          window.location.href = product.url;
        }
      });

      list.appendChild(item);
    });

    col.appendChild(list);
    return col;
  }

  private renderTrending(products: SuggestResponse['trending']): HTMLElement {
    if (!products || products.length === 0) {
      const empty = document.createElement('div');
      return empty;
    }

    const section = document.createElement('div');
    section.className = 'searchbox-trending';

    const title = document.createElement('div');
    title.className = 'searchbox-trending-title';
    title.textContent = '🔥 Trendy produkty';
    section.appendChild(title);

    const grid = document.createElement('div');
    grid.className = 'searchbox-trending-grid';

    products.forEach((product) => {
      const item = document.createElement('div');
      item.className = 'searchbox-trending-item';
      item.dataset.url = product.url;

      item.innerHTML = `
        <div class="searchbox-trending-image">
          <img src="${this.escapeHtml(product.image_url)}" alt="${this.escapeHtml(product.name)}" onerror="this.style.display='none'">
        </div>
        <div class="searchbox-trending-info">
          <div class="searchbox-trending-name">${this.escapeHtml(product.name)}</div>
          <div class="searchbox-trending-price">${this.formatPrice(product.price, product.currency)}</div>
          ${product.availability ? `<div class="searchbox-trending-stock">${this.escapeHtml(product.availability)}</div>` : ''}
        </div>
        <div class="searchbox-trending-cart">
          <button type="button" class="searchbox-cart-btn" data-product-url="${this.escapeHtml(product.url)}" aria-label="Pridať do košíka">
            🛒
          </button>
        </div>
      `;

      item.addEventListener('click', (e) => {
        if ((e.target as HTMLElement).closest('.searchbox-cart-btn')) {
          e.stopPropagation();
          this.handleAddToCart(product.url);
        } else {
          this.trackClick(product.id);
          window.location.href = product.url;
        }
      });

      grid.appendChild(item);
    });

    section.appendChild(grid);
    return section;
  }

  private trackSearch(query: string, data: SuggestResponse): void {
    try {
      const resultsCount = data.categories.length + data.top.length + data.products.length;
      
      fetch(`${this.options.apiBaseUrl}/analytics/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query,
          resultsCount,
        }),
      }).catch(() => {
        // Silently fail
      });
    } catch (error) {
      // Silently fail
    }
  }

  private trackClick(productId?: string, category?: string): void {
    if (!this.currentResults || !this.input) return;
    
    try {
      const query = this.input.value.trim();
      const resultsCount = 
        this.currentResults.categories.length + 
        this.currentResults.top.length + 
        this.currentResults.products.length;
      
      if (productId) {
        sessionStorage.setItem('lastClickedProductId', productId);
      }
      
      fetch(`${this.options.apiBaseUrl}/analytics/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query,
          resultsCount,
          clickedProductId: productId,
          clickedCategory: category,
        }),
      }).catch(() => {
        // Silently fail
      });
    } catch (error) {
      // Silently fail
    }
  }

  private async loadAndRenderRecommendations(container: HTMLElement, productId: string, appendToColumn: boolean = false): Promise<void> {
    try {
      const url = new URL(`${this.options.apiBaseUrl}/recommend`);
      url.searchParams.set('productId', productId);
      url.searchParams.set('type', 'similar');
      url.searchParams.set('limit', '6');

      const response = await fetch(url.toString());
      if (!response.ok) return;

      const data = await response.json();
      const products = data.products || [];

      if (products.length === 0) return;

      const section = document.createElement('div');
      section.className = 'searchbox-recommendations';

      const title = document.createElement('div');
      title.className = 'searchbox-recommendations-title';
      title.textContent = 'Odporúčané produkty';
      section.appendChild(title);

      const grid = document.createElement('div');
      grid.className = 'searchbox-recommendations-grid';

      products.forEach((product: any) => {
        const item = document.createElement('div');
        item.className = 'searchbox-recommendations-item';

        if (appendToColumn) {
          item.style.display = 'flex';
          item.style.flexDirection = 'row';
          item.style.alignItems = 'center';
          item.style.gap = '12px';
          item.style.padding = '10px 12px';
          
          const imageHtml = product.image_url
            ? `<div style="width: 60px; height: 60px; flex-shrink: 0; border-radius: 4px; overflow: hidden; background: #f8f8f8;"><img src="${this.escapeHtml(product.image_url)}" alt="${this.escapeHtml(product.name)}" style="width: 100%; height: 100%; object-fit: cover;" onerror="this.style.display='none'"></div>`
            : '<div style="width: 60px; height: 60px; flex-shrink: 0; background: #f8f8f8; border-radius: 4px;"></div>';

          item.innerHTML = `
            ${imageHtml}
            <div style="flex: 1; display: flex; flex-direction: column; gap: 4px; min-width: 0;">
              <div style="font-size: 13px; color: #333; line-height: 1.4; overflow: hidden; text-overflow: ellipsis; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;">${this.escapeHtml(product.name)}</div>
              <div style="font-size: 15px; font-weight: 600; color: #d32f2f;">${this.formatPrice(product.price, product.currency)}</div>
              ${product.availability ? `<div style="font-size: 12px; color: #2e7d32; font-weight: 500;">${this.escapeHtml(product.availability)}</div>` : ''}
            </div>
          `;
        } else {
          const imageHtml = product.image_url
            ? `<div class="searchbox-recommendations-image"><img src="${this.escapeHtml(product.image_url)}" alt="${this.escapeHtml(product.name)}" onerror="this.style.display='none'"></div>`
            : '';

          item.innerHTML = `
            ${imageHtml}
            <div class="searchbox-recommendations-info">
              <div class="searchbox-recommendations-name">${this.escapeHtml(product.name)}</div>
              <div class="searchbox-recommendations-price">${this.formatPrice(product.price, product.currency)}</div>
              ${product.availability ? `<div class="searchbox-recommendations-availability">${this.escapeHtml(product.availability)}</div>` : ''}
            </div>
          `;
        }

        item.addEventListener('click', () => {
          this.trackClick(product.id);
          window.location.href = product.product_url || product.url;
        });

        grid.appendChild(item);
      });

      section.appendChild(grid);
      
      if (appendToColumn) {
        container.appendChild(section);
      } else {
        container.appendChild(section);
      }
    } catch (error) {
      // Silently fail
    }
  }

  private handleAddToCart(productUrl: string): void {
    const urlObj = new URL(productUrl, window.location.origin);
    const productId = urlObj.pathname.split('/').pop();

    const endpoints = [
      `/cart/add?product=${productId}`,
      `/kosik/pridat?product=${productId}`,
      `/add-to-cart?product=${productId}`,
    ];

    let tried = false;
    for (const endpoint of endpoints) {
      fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ product: productId }),
      })
        .then((res) => {
          if (res.ok) {
            tried = true;
            console.log('Product added to cart');
          }
        })
        .catch(() => {
          // Continue to next endpoint
        });
    }

    if (!tried) {
      window.location.href = productUrl;
    }
  }

  private createOverlay(): void {
    if (!this.input) return;

    const existing = document.querySelector('.searchbox-overlay');
    if (existing) {
      existing.remove();
    }
    
    // Create backdrop for mobile
    if (this.isMobile) {
      this.createBackdrop();
    }

    this.overlay = document.createElement('div');
    this.overlay.className = 'searchbox-overlay';
    if (this.isMobile) {
      this.overlay.classList.add('searchbox-mobile-sheet');
    }

    this.overlay.innerHTML = '<div class="searchbox-container"></div>';
    
    // Mobile: Add swipe-to-close gestures
    if (this.isMobile) {
      this.attachSwipeGestures();
    }

    // Position update only on resize, NOT on scroll
    const updatePosition = () => {
      if (!this.input || !this.overlay || !this.isOpen) return;
      
      // Re-apply show() to update position with correct styles
      if (this.isOpen) {
        this.show();
      }
    };
    
    // Only update on window resize, NOT on scroll
    window.addEventListener('resize', updatePosition);

    document.body.appendChild(this.overlay);
  }
  
  private createBackdrop(): void {
    // Remove existing backdrop
    const existing = document.querySelector('.searchbox-backdrop');
    if (existing) {
      existing.remove();
    }
    
    this.backdrop = document.createElement('div');
    this.backdrop.className = 'searchbox-backdrop';
    this.backdrop.style.cssText = `
      position: fixed !important;
      top: 0 !important;
      left: 0 !important;
      right: 0 !important;
      bottom: 0 !important;
      background: rgba(0, 0, 0, 0.5) !important;
      z-index: 99 !important;
      opacity: 0 !important;
      transition: opacity 0.3s ease-out !important;
      display: none !important;
    `;
    
    // Click backdrop to close
    this.backdrop.addEventListener('click', () => {
      this.close();
    });
    
    document.body.appendChild(this.backdrop);
  }
  
  private attachSwipeGestures(): void {
    if (!this.overlay) return;
    
    const container = this.overlay.querySelector('.searchbox-container') as HTMLElement;
    if (!container) return;
    
    // Add drag handle visual indicator
    const handle = document.createElement('div');
    handle.className = 'searchbox-drag-handle';
    handle.style.cssText = `
      width: 40px;
      height: 4px;
      background: #ccc;
      border-radius: 2px;
      margin: 8px auto;
      cursor: grab;
    `;
    container.insertBefore(handle, container.firstChild);
    
    let startY = 0;
    let currentY = 0;
    let isDragging = false;
    
    const onTouchStart = (e: TouchEvent) => {
      startY = e.touches[0].clientY;
      isDragging = true;
      if (this.overlay) {
        this.overlay.style.transition = 'none';
      }
    };
    
    const onTouchMove = (e: TouchEvent) => {
      if (!isDragging || !this.overlay) return;
      
      currentY = e.touches[0].clientY;
      const diff = currentY - startY;
      
      // Only allow swipe down
      if (diff > 0) {
        this.overlay.style.transform = `translateY(${diff}px)`;
      }
    };
    
    const onTouchEnd = () => {
      if (!isDragging || !this.overlay) return;
      
      isDragging = false;
      const diff = currentY - startY;
      
      if (this.overlay) {
        this.overlay.style.transition = 'transform 0.3s ease-out';
      }
      
      // Close if swiped down more than 100px
      if (diff > 100) {
        this.close();
      } else {
        // Snap back to position
        if (this.overlay) {
          this.overlay.style.transform = 'translateY(0)';
        }
      }
    };
    
    this.overlay.addEventListener('touchstart', onTouchStart, { passive: true });
    this.overlay.addEventListener('touchmove', onTouchMove, { passive: true });
    this.overlay.addEventListener('touchend', onTouchEnd, { passive: true });
  }

  private show(): void {
    if (this.overlay) {
      if (!this.input) return;
      const rect = this.input.getBoundingClientRect();
      
      if (this.isMobile) {
        // Show backdrop
        if (this.backdrop) {
          this.backdrop.style.display = 'block';
          // Trigger reflow for animation
          setTimeout(() => {
            if (this.backdrop) {
              this.backdrop.style.opacity = '1';
            }
          }, 10);
        }
        
        // MOBILE: Bottom sheet overlay (like mobile apps)
        this.overlay.style.cssText = `
          position: fixed !important;
          bottom: 0 !important;
          left: 0 !important;
          right: 0 !important;
          top: auto !important;
          max-height: 70vh !important;
          background: white !important;
          z-index: 100 !important;
          display: block !important;
          visibility: visible !important;
          opacity: 1 !important;
          overflow-y: auto !important;
          box-shadow: 0 -4px 20px rgba(0,0,0,0.2) !important;
          border-top: 1px solid #e0e0e0 !important;
          border-radius: 16px 16px 0 0 !important;
          transform: translateY(0) !important;
          transition: transform 0.3s ease-out !important;
        `;
        
        // Make sure search input stays visible at top
        if (this.input) {
          this.input.style.position = 'relative';
          this.input.style.zIndex = '999999';
          this.input.style.background = 'white';
        }
      } else {
        // DESKTOP: Fixed full-width dropdown below search input
        this.overlay.style.cssText = `
          position: fixed !important;
          top: ${rect.bottom + 4}px !important;
          left: 20px !important;
          right: 20px !important;
          width: auto !important;
          max-width: 1400px !important;
          margin: 0 auto !important;
          max-height: 75vh !important;
          background: white !important;
          z-index: 99999 !important;
          display: block !important;
          visibility: visible !important;
          opacity: 1 !important;
          overflow-y: auto !important;
          box-shadow: 0 8px 30px rgba(0,0,0,0.15) !important;
          border-radius: 8px !important;
          padding: 20px !important;
        `;
      }
      
      this.isOpen = true;
      this.selectedIndex = -1;
      document.body.classList.add('searchbox-active');
    }
  }

  private close(): void {
    // Mobile: Animate out bottom sheet
    if (this.isMobile && this.overlay) {
      this.overlay.style.transform = 'translateY(100%)';
      
      // Hide backdrop
      if (this.backdrop) {
        this.backdrop.style.opacity = '0';
        setTimeout(() => {
          if (this.backdrop) {
            this.backdrop.style.display = 'none';
          }
        }, 300);
      }
      
      // Hide overlay after animation
      setTimeout(() => {
        if (this.overlay) {
          this.overlay.style.display = 'none';
        }
      }, 300);
    } else if (this.overlay) {
      // Desktop: immediate hide
      this.overlay.style.display = 'none';
    }
    
    this.isOpen = false;
    document.body.classList.remove('searchbox-active');
    this.selectedIndex = -1;
  }

  private handleKeydown(e: KeyboardEvent): void {
    if (!this.isOpen || !this.currentResults) return;

    const items = Array.from(this.overlay!.querySelectorAll('.searchbox-item, .searchbox-all-results a'));
    const maxIndex = items.length - 1;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        this.selectedIndex = Math.min(this.selectedIndex + 1, maxIndex);
        this.updateSelection(items);
        break;

      case 'ArrowUp':
        e.preventDefault();
        this.selectedIndex = Math.max(this.selectedIndex - 1, -1);
        this.updateSelection(items);
        break;

      case 'Enter':
        e.preventDefault();
        if (this.selectedIndex >= 0 && items[this.selectedIndex]) {
          const item = items[this.selectedIndex];
          if (item instanceof HTMLAnchorElement) {
            window.location.href = item.href;
          } else {
            const url = item.getAttribute('href') || item.dataset.url;
            if (url) {
              window.location.href = url;
            }
          }
        }
        break;

      case 'Escape':
        this.close();
        break;
    }
  }

  private updateSelection(items: Element[]): void {
    items.forEach((item, index) => {
      if (index === this.selectedIndex) {
        item.classList.add('searchbox-selected');
        item.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      } else {
        item.classList.remove('searchbox-selected');
      }
    });
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  private formatPrice(price: number, currency: string): string {
    return new Intl.NumberFormat(this.options.lang || 'sk', {
      style: 'currency',
      currency: currency || 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(price);
  }
}

// Export to window
declare global {
  interface Window {
    SearchBox: typeof SearchBox;
    searchBoxInit: (options: SearchBoxOptions) => void;
  }
}

// Export SearchBox class
(window as any).SearchBox = SearchBox;

window.searchBoxInit = function (options: SearchBoxOptions) {
  const init = () => {
    try {
      new SearchBox(options);
    } catch (error) {
      console.error('SearchBox initialization failed:', error);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
};
